import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";

import { Header } from "../layout/Header";
import { useAppHeaderProps } from "../../lib/useAppHeaderProps";
import { getStoredCurrentUser } from "../../lib/app-events";
import { CurrentUser } from "../../types/user";

// Owner-review note: this text describes what the code actually collects and
// which services receive it (Google Sign-In, Google Analytics, Gmail SMTP,
// Google Translate / text-to-speech). Have it reviewed before relying on it legally.
const CONTACT_EMAIL = "info@upspring.it";
const LAST_UPDATED = "21 October 2026";

type Section = { heading: string; body: React.ReactNode };

const PRIVACY_SECTIONS: Section[] = [
  {
    heading: "What we collect",
    body: (
      <ul>
        <li>
          <b>Account details:</b> your name, email address, username and profile photo. If you sign in with Google we
          also store your Google account ID. If you sign up with email we store your password as a one-way hash, never
          in plain text.
        </li>
        <li>
          <b>What you post and do:</b> quotes, articles, books, videos, AI prompts and AI topics you share, plus your
          comments, tags, likes and the people you follow.
        </li>
        <li>
          <b>Preferences:</b> your light/dark theme and whether you get the daily digest and follow emails.
        </li>
        <li>
          <b>Verification codes:</b> short-lived codes we email you to verify your address or reset your password.
        </li>
        <li>
          <b>Usage data:</b> Google Analytics records the pages you view, how far you scroll, your device and browser
          type, approximate location derived from your IP address, and a random analytics identifier.
        </li>
        <li>
          <b>Feedback:</b> anything you send us through Send feedback.
        </li>
      </ul>
    ),
  },
  {
    heading: "How we use it",
    body: (
      <ul>
        <li>To run The Deepam: sign you in, show your posts and profile, and count likes and comments.</li>
        <li>To email you verification codes, password resets, the daily digest and follow notifications. You can turn the digest and follow emails off from your account menu or the link in any email.</li>
        <li>To understand which features people use and improve them.</li>
      </ul>
    ),
  },
  {
    heading: "Who can see it and who we share it with",
    body: (
      <>
        <p>
          Your posts, comments, tags, username, name, profile photo and follows are public on The Deepam. Your email
          address is never shown to other users.
        </p>
        <p>We don't sell your data. We use these services to run The Deepam, and they process data on our behalf:</p>
        <ul>
          <li>Google Sign-In, to let you sign in with your Google account.</li>
          <li>Google Analytics, for usage statistics.</li>
          <li>Google (Gmail), to send our emails.</li>
          <li>Google Translate, when you translate a quote or have text read aloud.</li>
        </ul>
      </>
    ),
  },
  {
    heading: "How long we keep it",
    body: (
      <p>
        We keep your account and content until you delete them. Verification and reset codes expire within minutes.
        Google Analytics data is kept according to our Google Analytics retention setting.
      </p>
    ),
  },
  {
    heading: "Deleting your account",
    body: (
      <p>
        Signed in, open the account menu (your photo at the top right) and choose <b>Delete account</b>. This
        permanently deletes your account and everything you've posted, commented, tagged, liked or followed. You can
        also email <span className="font-medium select-all">{CONTACT_EMAIL}</span> from your account's address and
        we'll delete it for you.
      </p>
    ),
  },
  {
    heading: "Security",
    body: <p>All traffic between the app or website and our servers is encrypted with HTTPS. Passwords are hashed.</p>,
  },
  {
    heading: "Children",
    body: <p>The Deepam isn't directed at children under 13, and we don't knowingly collect their data.</p>,
  },
  {
    heading: "Changes and contact",
    body: (
      <p>
        If we change this policy we'll update the date above. Questions or requests:{" "}
        <span className="font-medium select-all">{CONTACT_EMAIL}</span>.
      </p>
    ),
  },
];

const TERMS_SECTIONS: Section[] = [
  {
    heading: "Using The Deepam",
    body: (
      <p>
        The Deepam is a community for sharing quotes, articles, books, videos and AI prompts. By creating an account
        or using the app or website you agree to these terms and to our <Link to="/privacy">Privacy Policy</Link>.
      </p>
    ),
  },
  {
    heading: "Your account",
    body: (
      <p>
        Keep your sign-in details to yourself; you're responsible for what's posted from your account. You can delete
        your account at any time from the account menu.
      </p>
    ),
  },
  {
    heading: "What you post",
    body: (
      <>
        <p>
          You keep ownership of what you post. By posting, you let The Deepam display, store and share it on the
          service. Only post content you have the right to share, and credit authors and sources.
        </p>
        <p>Don't post content that is illegal, hateful, harassing, sexually explicit, spam, or that infringes someone else's rights.</p>
      </>
    ),
  },
  {
    heading: "Reporting content",
    body: (
      <p>
        Use <b>Report</b> on any post, or Send feedback, to tell us about content that breaks these terms. We review
        reports and may remove content or suspend accounts that break them.
      </p>
    ),
  },
  {
    heading: "The service",
    body: (
      <p>
        We provide The Deepam as is and may change or stop features. Links to other sites are provided for
        convenience; we aren't responsible for their content.
      </p>
    ),
  },
  {
    heading: "Contact",
    body: (
      <p>
        Questions about these terms: <span className="font-medium select-all">{CONTACT_EMAIL}</span>.
      </p>
    ),
  },
];

// /privacy and /terms. Linked from the sign-in dialog and the store listings.
export function LegalPage({ kind }: { kind: "privacy" | "terms" }) {
  const { onOpenAuth, onOpenCreateModal } = useAppHeaderProps();
  const [currentUser] = useState<CurrentUser | null>(getStoredCurrentUser);
  const title = kind === "privacy" ? "Privacy Policy" : "Terms of Service";
  const sections = kind === "privacy" ? PRIVACY_SECTIONS : TERMS_SECTIONS;

  useEffect(() => {
    document.title = `${title} · The Deepam`;
    window.scrollTo(0, 0);
  }, [title]);

  return (
    <div className="max-w-[85rem] mx-auto px-3 py-6">
      <Header currentUser={currentUser} onOpenAuth={onOpenAuth} onOpenCreateModal={onOpenCreateModal} />
      <article className="mx-auto mt-6 max-w-2xl text-slate-700 dark:text-slate-300">
        <h1 className="text-2xl sm:text-3xl font-bold text-slate-900 dark:text-slate-100">{title}</h1>
        <p className="mt-1 text-sm text-slate-500 dark:text-slate-400">Last updated {LAST_UPDATED}</p>
        {sections.map((section) => (
          <section key={section.heading} className="mt-7">
            <h2 className="text-lg font-semibold text-slate-900 dark:text-slate-100">{section.heading}</h2>
            <div className="mt-2 space-y-3 text-[15px] leading-relaxed [&_a]:underline [&_ul]:list-disc [&_ul]:space-y-2 [&_ul]:pl-5">
              {section.body}
            </div>
          </section>
        ))}
      </article>
    </div>
  );
}
